import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Button from "../../../../../components/base/button/button";
import IconButton from "../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import GiftCardSummarySearchSection, {GiftCardSummaryDateOptionRanges} from "../../../../../components/search-sections/gift-card-transaction-summary";
import Api from "../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import {GiftCardTransactionsDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {useHistory, useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../redux";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const dataGridSaveConfig = {
    name: 'UserGiftCardSummariesViewDataGrid',
    version: 1,
}

const CustomerGiftCardSummariesView = () => {
    const params = useParams()
    const history = useHistory()
    const [cardLoading, setCardLoading] = useState(true);
    const [cardHistories, setCardHistories] = useState([]);
    const [cardInfo, setCardInfo] = useState();
    const isMounted = useIsMounted();
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('customer-gift-card-summaries-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((tags) => tags.customerCardSummaryGiftCards(defaultCustomer?.code, params.id));
        configureBehavioralTool((tags) => tags.customerCardSummaryGiftCards(defaultCustomer?.code, params.id));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer?.code, params.id]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: 'Submit Date & Time',
                name: GiftCardTransactionsDataGridCellKeys.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
                width: 130,
            },
            {
                title: "Type",
                name: GiftCardTransactionsDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: "Amount",
                name: GiftCardTransactionsDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Balance",
                name: GiftCardTransactionsDataGridCellKeys.balance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "User Order",
                name: GiftCardTransactionsDataGridCellKeys.userOrder,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 120,
            },

        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Fetches the card information from the server with the provided id in the url params.
     *
     * if the result of the api is successful, sets the internal state
     * @return {Promise<void>}
     */
    const getCardInformation = useCallback(async () => {
        setCardLoading(true);
        const response = await Api.getCustomerGiftCards(params.customerId);
        if (!isMounted())
            return;
        setCardInfo(response?.data?.find(e => e.id === Number(params.id)));
        setCardLoading(false);
    }, [params.customerId, params.id, isMounted])

    /**
     * Fetches the card information from the server with the provided id in the url params.
     */
    useEffect(() => {
        void getCardInformation();
    }, [getCardInformation])

    /**
     * Gets the list of gift card transaction by calling the api regarding this action
     * Populates the [cardHistories] in the inner state with the response if the api call was successful
     *
     * @param cancelTokenSource
     * @param {boolean} isForExport for indicating if the callback is used for exporting purposes
     * @return {Promise<ApiResponseDs<*> | void>}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {

        const defaultPeriod = GiftCardSummaryDateOptionRanges.last30Days();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        const forApi = {
            cardId: Number(params.id),
            startDate: args.filters?.startDate ?? defaultPeriod.startDate,
            endDate: args.filters?.endDate ?? defaultPeriod.endDate,
        };

        const response = await Api.searchGiftCardHistory(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setCardHistories(response.data ?? []);
        }
        if (forExport)
            return response;
    }, [isMounted, params.id])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const searchData = useSearch(search)
    const canExportCustomerGiftCards = operatorAccessLevels.packages.sales.exportCustomerGiftCards;


    /**
     * Formats the list of gift card transactions into csv format
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Gift Card ${cardInfo?.cardNumber} Summary`
        return {
            title: exportTitle,
            body: list?.map(c => {
                const recordInfo = JSON.parse(c.recordId);
                return {
                    'Submit Date Time': c.date ? DateUtils.serverToLocal(c.date).format(DateUtils.dateFormat) : '',
                    'Type': c.type?.name ?? '',
                    'Amount': formatMoney(c.amount),
                    'New Balance': formatMoney(c.newBalance),
                    [recordInfo?.Name ?? "Record Id"]: recordInfo?.Value ?? '',
                }
            }) ?? [],
        }
    }, [cardInfo])

    /**
     * Exports the requests using the same search filters to csv
     */
    const exportToCsv = useCallback(async () => {
        const response = await fetchEntries(searchData, true);
        if (!isMounted())
            return;
        if (!response?.resultFlag || !response?.data?.length)
            return;
        return formatExportingData(response.data);
    }, [fetchEntries, isMounted, searchData, formatExportingData])


    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return cardHistories?.map(cardHistory => ({
            key: cardHistory?.id,
            data: cardHistory,
            cells: {
                [GiftCardTransactionsDataGridCellKeys.submitDateTime]: cardHistory?.date,
                [GiftCardTransactionsDataGridCellKeys.type]: cardHistory?.type?.name,
                [GiftCardTransactionsDataGridCellKeys.amount]: cardHistory?.amount,
                [GiftCardTransactionsDataGridCellKeys.balance]: cardHistory?.newBalance,
                [GiftCardTransactionsDataGridCellKeys.userOrder]: () => {
                    try {
                        const recordInfo = JSON.parse(cardHistory.recordId);
                        if (recordInfo.Name === 'Quote Id') {
                            return (
                                <>
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={() => history.push(routeFunctions.customer.sales.quotes.single(params.customerId, recordInfo.Value))}
                                    >
                                        View Quote
                                    </Button>
                                </>
                            )
                        }
                        return <p {...dataGridCellTextProps}>{recordInfo.Name} : {recordInfo.Value}</p>
                    } catch (e) {
                        return '--';
                    }
                },

            }
        })) ?? []
    }, [cardHistories, history, params.customerId])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center mb-4'>
                <div className={'d-flex flex-column'}>
                    <div className={'d-flex w-100 justify-content-start' +
                        ' align-items-center'}>
                        <IconButton
                            onPressed={() => history.goBack()}
                            color={'paper.secondary'}
                        >
                            <ArrowBack className='icon normal'/>
                        </IconButton>
                        <h2 className={'text-xl font-weight-semi-bold m-0 ml-3'}>
                            Gift Card Usage Summary
                        </h2>
                    </div>
                </div>
            </Row>
            <GiftCardSummarySearchSection
                searchData={searchData}
                card={{
                    data: cardInfo,
                    loading: cardLoading,
                }}
            />
            <div className={'mb-5'}/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                visibleRows={rows.length}
                state={{
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportCustomerGiftCards ? exportToCsv : undefined}
                hideFooter
            />
        </>
    )
}

export default CustomerGiftCardSummariesView;
